import React, { useState } from "react";
import {
  Popover,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";
import {
  Highlight as HighlightIcon,
  Note as NoteIcon,
  HighlightOff as UnhighlightIcon,
} from "@mui/icons-material";

const TextHighlighterWrapper = ({
  children,
  onAddNote = null,
  className = "",
  excludeHeaders = true,
  highlights = [], // Array of highlight objects from parent
  onAddHighlight = null, // Callback to add highlight to parent state
  onRemoveHighlight = null, // Callback to remove highlight from parent state
  partKey = "default", // Unique key for this content section
}) => {
  const [anchorPos, setAnchorPos] = useState(null);
  const [selectedText, setSelectedText] = useState("");
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [clickedHighlight, setClickedHighlight] = useState(null);
  const [unhighlightMode, setUnhighlightMode] = useState(false);
  const containerRef = React.useRef(null);

  // Handle click on highlighted text
  const handleHighlightClick = React.useCallback((highlightSpan, event) => {
    const rect = highlightSpan.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const useTopPosition = spaceBelow < 100 && spaceAbove > 100;

    setClickedHighlight(highlightSpan);
    // Use fixed positioning relative to viewport
    setAnchorPos({
      top: useTopPosition ? rect.top - 10 : rect.bottom + 10,
      left: rect.left + rect.width / 2,
    });
    setUnhighlightMode(true);
  }, []);

  // Define createHighlightSpan before useEffect to avoid dependency issues
  const createHighlightSpan = React.useCallback(
    (highlightId) => {
      const span = document.createElement("span");

      // Add unique identifier for tracking
      const finalId =
        highlightId ||
        `highlight-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      span.setAttribute("data-highlight-id", finalId);
      span.setAttribute("data-highlight", "true");
      span.className = "text-highlight";

      // Enhanced styling to prevent UI disturbance - use CSS custom properties for consistency
      span.style.cssText = `
      background-color: #ffeb3b;
      padding: 0;
      margin: 0;
      border: 0;
      outline: 0;
      display: inline;
      line-height: inherit;
      font-size: inherit;
      font-weight: inherit;
      font-family: inherit;
      letter-spacing: inherit;
      word-spacing: inherit;
      text-decoration: inherit;
      vertical-align: baseline;
      box-sizing: border-box;
      position: static;
      float: none;
      clear: none;
      width: auto;
      height: auto;
      min-width: 0;
      min-height: 0;
      max-width: none;
      max-height: none;
      white-space: inherit;
      word-break: inherit;
      word-wrap: inherit;
      overflow-wrap: inherit;
      hyphens: inherit;
      text-align: inherit;
      text-indent: inherit;
      text-transform: inherit;
      cursor: pointer;
      transition: background-color 0.2s ease;
      transform: none;
      opacity: 1;
      visibility: visible;
      z-index: auto;
      pointer-events: auto;
    `;

      // Add event listeners
      span.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleHighlightClick(span, e);
      });

      span.addEventListener("mouseenter", () => {
        span.style.backgroundColor = "#fff176";
      });

      span.addEventListener("mouseleave", () => {
        span.style.backgroundColor = "#ffeb3b";
      });

      return span;
    },
    [handleHighlightClick],
  );

  // Restore highlights when content changes
  React.useEffect(() => {
    if (!containerRef.current || !highlights || highlights.length === 0) return;

    // Clear existing highlights first
    const existingHighlights = containerRef.current.querySelectorAll(
      '[data-highlight="true"]',
    );
    existingHighlights.forEach((span) => {
      const textContent = span.textContent;
      const textNode = document.createTextNode(textContent);
      span.parentNode.replaceChild(textNode, span);
    });

    // Restore highlights from state for current part
    const currentPartHighlights = highlights.filter(
      (h) => h.partKey === partKey,
    );

    currentPartHighlights.forEach((highlight) => {
      try {
        const walker = document.createTreeWalker(
          containerRef.current,
          NodeFilter.SHOW_TEXT,
          null,
        );

        let node;
        while ((node = walker.nextNode())) {
          const text = node.textContent;
          const index = text.indexOf(highlight.text);

          if (index !== -1) {
            const range = document.createRange();
            range.setStart(node, index);
            range.setEnd(node, index + highlight.text.length);

            const span = createHighlightSpan(highlight.id);
            try {
              range.surroundContents(span);
            } catch (e) {
              const contents = range.extractContents();
              span.appendChild(contents);
              range.insertNode(span);
            }
            break; // Only highlight first occurrence
          }
        }
      } catch (error) {
        console.warn("Failed to restore highlight:", error);
      }
    });
  }, [highlights, partKey, children, createHighlightSpan]);

  const handleMouseUp = (event) => {
    // Slightly longer delay to ensure selection is complete, especially for single words
    setTimeout(() => {
      const selection = window.getSelection();
      const selectedText = selection.toString().trim();

      console.log(
        "Selection detected:",
        selectedText,
        "Length:",
        selectedText.length,
      ); // Debug log

      // Allow selection of even single characters or words
      if (selectedText.length > 0 && selection.rangeCount > 0) {
        // Check if selection is within a header element (if excludeHeaders is true)
        if (excludeHeaders) {
          const range = selection.getRangeAt(0);
          const container = range.commonAncestorContainer;
          const element =
            container.nodeType === Node.TEXT_NODE
              ? container.parentElement
              : container;

          // Check if selection is within header tags
          const headerTags = ["H1", "H2", "H3", "H4", "H5", "H6"];
          const isInHeader = element.closest(
            headerTags.map((tag) => tag.toLowerCase()).join(","),
          );

          if (isInHeader) {
            console.log("Selection in header, ignoring"); // Debug log
            return;
          }
        }

        // Get the bounding rectangle of the selection
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        console.log("Setting anchor position:", rect); // Debug log

        // Position the popover above or below the selection based on available space
        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;
        const useTopPosition = spaceBelow < 150 && spaceAbove > 150;

        // Use fixed positioning relative to viewport (not scrollY)
        setAnchorPos({
          top: useTopPosition ? rect.top - 10 : rect.bottom + 10,
          left: rect.left + rect.width / 2, // Center horizontally
        });
        setSelectedText(selectedText);
      } else {
        console.log("No valid selection detected"); // Debug log
      }
    }, 100);
  };

  const handleHighlight = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    const range = selection.getRangeAt(0);
    const highlightId = `highlight-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Save highlight data to parent state if handler provided
    if (onAddHighlight) {
      onAddHighlight({
        id: highlightId,
        text: selectedText,
        partKey: partKey,
        timestamp: new Date().toISOString(),
      });
    }

    // Check if selection spans multiple elements - handle differently
    const startContainer = range.startContainer;
    const endContainer = range.endContainer;
    const spansMultipleElements = startContainer !== endContainer;

    if (spansMultipleElements) {
      // For complex selections, use a more robust approach
      handleComplexSelection(range, highlightId);
    } else {
      // For simple selections within same element, use direct approach
      handleSimpleSelection(range, highlightId);
    }

    selection.removeAllRanges();
    setAnchorPos(null);
    setSelectedText("");
  };

  const handleSimpleSelection = (range, highlightId) => {
    const span = createHighlightSpan(highlightId);

    try {
      // For simple selections, try surroundContents first
      range.surroundContents(span);
    } catch (e) {
      // If that fails, use extract and insert approach
      try {
        const contents = range.extractContents();
        span.appendChild(contents);
        range.insertNode(span);
      } catch (err) {
        console.warn("Failed to highlight selection:", err);
        // Fallback: create text node
        const selectedText = range.toString();
        const textNode = document.createTextNode(selectedText);
        span.appendChild(textNode);
        range.deleteContents();
        range.insertNode(span);
      }
    }
  };

  const handleComplexSelection = (range, highlightId) => {
    try {
      // For complex selections spanning multiple elements, use a more careful approach
      const walker = document.createTreeWalker(
        range.commonAncestorContainer,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: function (node) {
            const nodeRange = document.createRange();
            nodeRange.selectNode(node);

            // Check if this text node intersects with our selection
            if (range.intersectsNode(node)) {
              return NodeFilter.FILTER_ACCEPT;
            }
            return NodeFilter.FILTER_REJECT;
          },
        },
      );

      const textNodes = [];
      let node = walker.nextNode();
      while (node) {
        // Check if the text node is within our selection range
        if (range.intersectsNode(node)) {
          textNodes.push(node);
        }
        node = walker.nextNode();
      }

      // Process each text node that intersects with the selection
      textNodes.forEach((textNode) => {
        const nodeRange = document.createRange();
        nodeRange.selectNode(textNode);

        // Calculate the intersection with our selection
        const startOffset =
          textNode === range.startContainer ? range.startOffset : 0;
        const endOffset =
          textNode === range.endContainer
            ? range.endOffset
            : textNode.textContent.length;

        if (startOffset < endOffset) {
          // Create a range for just this portion
          const partialRange = document.createRange();
          partialRange.setStart(textNode, startOffset);
          partialRange.setEnd(textNode, endOffset);

          // Create highlight span for this portion
          const span = createHighlightSpan(highlightId);
          try {
            partialRange.surroundContents(span);
          } catch (e) {
            // If surrounding fails, extract and wrap
            const content = partialRange.extractContents();
            span.appendChild(content);
            partialRange.insertNode(span);
          }
        }
      });
    } catch (error) {
      console.warn(
        "Complex highlighting failed, falling back to simple method:",
        error,
      );
      // Fallback to simple selection method
      handleSimpleSelection(range);
    }
  };

  const handleUnhighlight = () => {
    if (!clickedHighlight) return;

    // Get highlight ID and remove from parent state
    const highlightId = clickedHighlight.getAttribute("data-highlight-id");
    if (highlightId && onRemoveHighlight) {
      onRemoveHighlight(highlightId);
    }

    // Get the text content
    const textContent = clickedHighlight.textContent;

    // Create a text node to replace the highlighted span
    const textNode = document.createTextNode(textContent);

    // Replace the highlight span with the text node
    clickedHighlight.parentNode.replaceChild(textNode, clickedHighlight);

    // Clean up state
    setAnchorPos(null);
    setClickedHighlight(null);
    setUnhighlightMode(false);

    // Merge adjacent text nodes to clean up the DOM
    const parent = textNode.parentNode;
    if (parent) {
      parent.normalize();
    }
  };

  const handleAddNote = () => {
    setAnchorPos(null);
    setNoteDialogOpen(true);
  };

  const handleSaveNote = () => {
    if (onAddNote && noteText.trim()) {
      onAddNote({
        selectedText: selectedText,
        userNote: noteText.trim(),
      });
    }
    // Also highlight the text when adding a note
    handleHighlight();
    setNoteDialogOpen(false);
    setNoteText("");
    setSelectedText("");
  };

  const handleCancelNote = () => {
    setNoteDialogOpen(false);
    setNoteText("");
    setSelectedText("");
    window.getSelection().removeAllRanges();
  };

  const handleClose = () => {
    setAnchorPos(null);
    setSelectedText("");
    setClickedHighlight(null);
    setUnhighlightMode(false);
    if (!unhighlightMode) {
      window.getSelection().removeAllRanges();
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        onMouseUp={handleMouseUp}
        className={className}
        style={{
          cursor: "text",
          userSelect: "text", // Enable text selection
          position: "relative",
        }}
        onMouseDown={(e) => {
          // Clear any existing selection when starting new selection
          setSelectedText("");
          setAnchorPos(null);
          console.log("Mouse down, clearing selection"); // Debug log
        }}
      >
        {children}
      </div>

      <Popover
        open={Boolean(anchorPos)}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={anchorPos}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{
          "& .MuiPopover-paper": {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            border: "1px solid rgba(0,0,0,0.1)",
            zIndex: 9999, // Ensure it's on top
            position: "fixed", // Use fixed positioning
          },
        }}
      >
        <Box
          sx={{
            p: 1,
            display: "flex",
            gap: 1,
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {unhighlightMode ? (
            // Unhighlight mode - show remove highlight option
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.7rem" }}
              >
                Remove highlight?
              </Typography>
              <IconButton
                onClick={handleUnhighlight}
                size="small"
                sx={{
                  bgcolor: "#f44336",
                  color: "white",
                  "&:hover": { bgcolor: "#d32f2f" },
                }}
              >
                <UnhighlightIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            // Normal highlight mode
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                onClick={handleHighlight}
                size="small"
                sx={{
                  bgcolor: "#9D2235",
                  color: "white",
                  "&:hover": { bgcolor: "#7a182a" },
                }}
              >
                <HighlightIcon fontSize="small" />
              </IconButton>

              {onAddNote && (
                <IconButton
                  onClick={handleAddNote}
                  size="small"
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    "&:hover": { bgcolor: "#1565c0" },
                  }}
                >
                  <NoteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Popover>

      {/* Note Input Dialog */}
      <Dialog
        open={noteDialogOpen}
        onClose={handleCancelNote}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#9D2235", color: "white" }}>
          Add Note
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, color: "text.secondary" }}
          >
            Selected text:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              p: 1,
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              fontStyle: "italic",
              borderLeft: "3px solid #9D2235",
            }}
          >
            "{selectedText}"
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Your note"
            placeholder="Type your note here..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#9D2235",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#9D2235",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCancelNote} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNote}
            variant="contained"
            disabled={!noteText.trim()}
            sx={{
              bgcolor: "#9D2235",
              "&:hover": { bgcolor: "#7a182a" },
            }}
          >
            Save Note
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TextHighlighterWrapper;
